/**
 * The post-answer feedback ladder (D1, #367) — what a learner may be shown
 * AFTER a wrong (✗) or missing (◌) answer, and what each step costs the
 * evidence.
 *
 * **Every rung is a recorded delivery with a D15 kind.** The pre-answer
 * assistance ladder (`assistance.ts`) is about help requested before a response.
 * This is the same invariant 1 applied after one: a fix that follows feedback is
 * evidence only if the help that preceded it is on the record, and
 * `deriveIndependence` — the only function permitted to decide it — decides what
 * the fix is worth. This module declares kinds; it never decides independence.
 *
 * **No rung is `none` or `re_presentation`.** Telling a learner what was wrong,
 * hinting at the repair, exhibiting a counterexample, or showing the solution
 * all supply information the learner did not have. A post-answer rung that
 * preserved independence would be a rung that manufactures it.
 *
 * **The default rung is delivered whether or not the learner pulled it
 * (founder, 2026-10-15; D30 as amended 2026-10-16).** Error type and location
 * is shown by default, so it is recorded by default: showing it and not
 * recording it is the false accept this whole file exists to prevent.
 *
 * **Before the due date the ladder stops at the counterexample.** The worked
 * solution stays closed until the due date (D30, amended 2026-10-16), and
 * `postAnswerRungOpen` fails closed: an unknown due date is a closed rung.
 *
 * Pure: imports only from `./assistance.js`.
 */

import type { AssistanceKind, DeliveredAssistance } from './assistance.js';

export const POST_ANSWER_RUNGS = Object.freeze([
  'error_location',
  'hint',
  'counterexample',
  'solution',
] as const);

export type PostAnswerRung = (typeof POST_ANSWER_RUNGS)[number];

export function isPostAnswerRung(value: unknown): value is PostAnswerRung {
  return typeof value === 'string' && (POST_ANSWER_RUNGS as readonly string[]).includes(value);
}

/**
 * The version of this ladder. Any change to a rung's `kind`, to which rung is
 * shown by default, or to which rung is closed before the due date is a new
 * version: it changes what a fix after feedback is worth, which is a claim
 * about the learner.
 */
export const POST_ANSWER_LADDER_VERSION = 'post-answer-ladder@1';

export interface PostAnswerRungDefinition {
  readonly summary: string;
  /** What the rung does to the evidence (D15). */
  readonly kind: AssistanceKind;
  /** Delivered, and therefore recorded, without the learner pulling it. */
  readonly shownByDefault: boolean;
  /** Not available before the due date (D30, amended 2026-10-16). */
  readonly closedBeforeDue: boolean;
}

function rung(definition: PostAnswerRungDefinition): PostAnswerRungDefinition {
  return Object.freeze({ ...definition });
}

export const POST_ANSWER_LADDER: Readonly<Record<PostAnswerRung, PostAnswerRungDefinition>> = Object.freeze({
  error_location: rung({
    summary: 'What kind of error it is, and where it is. Shown by default.',
    kind: 'informative',
    shownByDefault: true,
    closedBeforeDue: false,
  }),
  hint: rung({
    summary: 'A pointer toward the repair, short of the repair itself.',
    kind: 'informative',
    shownByDefault: false,
    closedBeforeDue: false,
  }),
  counterexample: rung({
    summary: 'A certified counterexample to the learner’s claim.',
    kind: 'revealing',
    shownByDefault: false,
    closedBeforeDue: false,
  }),
  solution: rung({
    summary: 'The worked solution. Closed until the due date.',
    kind: 'revealing',
    shownByDefault: false,
    closedBeforeDue: true,
  }),
});

export interface PostAnswerRungOpenness {
  readonly open: boolean;
  readonly reason: string;
}

/**
 * Whether a rung may be shown now. Fails closed: a rung that is closed before
 * the due date is closed when the due date is unknown (`null`) or has not
 * arrived; it opens at the due date itself. Every other rung is always open.
 */
export function postAnswerRungOpen(
  rungName: PostAnswerRung,
  context: { readonly now: Date; readonly dueAt: Date | null },
): PostAnswerRungOpenness {
  if (!POST_ANSWER_LADDER[rungName].closedBeforeDue) {
    return { open: true, reason: `The ${rungName} rung is open at any time.` };
  }
  const { now, dueAt } = context;
  if (dueAt === null || Number.isNaN(dueAt.getTime())) {
    return { open: false, reason: `The ${rungName} rung is closed: no due date is known, and an unknown due date is not a passed one.` };
  }
  if (Number.isNaN(now.getTime()) || now.getTime() < dueAt.getTime()) {
    return { open: false, reason: `The ${rungName} rung is closed until the due date.` };
  }
  return { open: true, reason: `The due date has passed, so the ${rungName} rung is open.` };
}

/**
 * The delivery a post-answer rung is, in the form `deriveIndependence`
 * consumes. `level` is 0 because no pre-answer ladder rung was requested; the
 * decision is carried by `kind`, which is the rung's declared kind, and by
 * `revealsAnswer`, derived from it. Feeding this to `deriveIndependence` is how
 * a post-answer rung is judged — nothing here decides.
 */
export function postAnswerDelivery(rungName: PostAnswerRung, deliveredAt: Date): DeliveredAssistance {
  const kind = POST_ANSWER_LADDER[rungName].kind;
  return { level: 0, kind, revealsAnswer: kind === 'revealing', deliveredAt };
}
